import type { FC } from 'react';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { CDN_URL } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/demo-data-button/constants';
import { FACE_DETECTOR_LOADING_TIMEOUT } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/constants';
import type {
    Detector,
    FaceDetectorContextProps,
    FaceDetectorContextProviderProps,
    TfliteGlobal,
} from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/types';
import { fetchTfliteFaceModels } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/utils';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import type { RootState } from '@store/root-reducer';

const TFJS_SCRIPTS_ATTR = 'data-tfjs-tflite-script';
const TF_LITE_SRC = `${CDN_URL}/tfjs-tflite/tf-tflite-0.0.1-alpha.10.min.js`;
const TF_CORE_SRC = `${CDN_URL}/tfjs-tflite/tfjs-core-4.22.0`;

const noop = () => undefined;

export const FaceDetectorContext = createContext<FaceDetectorContextProps>({
    tfLiteModel: null,
    tfLiteLandmarkModel: null,
    imageQualityModel: null,
    isTfliteScriptReady: false,
    hasFaceDetectorLoadError: false,
    refetchTfliteScripts: noop,
    refetchFaceModels: noop,
});

const removeTfliteDomAssets = (container: HTMLElement) => {
    container
        .querySelectorAll(`script[${TFJS_SCRIPTS_ATTR}], link[data-tfjs-tflite-preload]`)
        .forEach((el) => el.remove());
};

export const FaceDetectorContextProvider: FC<FaceDetectorContextProviderProps> = ({ children }) => {
    const [tfLiteModel, setTfLiteModel] = useState<Detector | null>(null);
    const [tfLiteLandmarkModel, setTfLiteLandmarkModel] = useState<Detector | null>(null);
    const [imageQualityModel, setImageQualityModel] = useState<Detector | null>(null);
    const [isTfliteScriptReady, setIsTfliteScriptReady] = useState(false);
    const [hasFaceDetectorLoadError, setHasFaceDetectorLoadError] = useState(false);
    const [scriptsReloadKey, setScriptsReloadKey] = useState(0);
    const [modelsReloadKey, setModelsReloadKey] = useState(0);
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const { containerRef } = useWidgetContext();

    const clearFaceModels = useCallback(() => {
        setTfLiteModel(null);
        setTfLiteLandmarkModel(null);
        setImageQualityModel(null);
    }, []);

    const refetchTfliteScripts = useCallback(() => {
        const container = containerRef.current;

        if (container) {
            removeTfliteDomAssets(container);
        }

        setHasFaceDetectorLoadError(false);
        setIsTfliteScriptReady(false);
        clearFaceModels();
        setScriptsReloadKey((key) => key + 1);
    }, [clearFaceModels, containerRef]);

    const refetchFaceModels = useCallback(() => {
        setHasFaceDetectorLoadError(false);
        clearFaceModels();
        setModelsReloadKey((key) => key + 1);
    }, [clearFaceModels]);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container || !sdkOptions?.settings?.face_comparison_required) {
            setIsTfliteScriptReady(false);

            return;
        }

        const markTfliteReady = () => {
            if ((window as TfliteGlobal).tflite?.loadTFLiteModel) {
                setHasFaceDetectorLoadError(false);
                setIsTfliteScriptReady(true);
            }
        };

        const markTfliteScriptError = () => {
            setIsTfliteScriptReady(false);
            setHasFaceDetectorLoadError(true);
        };

        if ((window as TfliteGlobal).tflite?.loadTFLiteModel) {
            setHasFaceDetectorLoadError(false);
            setIsTfliteScriptReady(true);

            return;
        }

        const existingTfliteScript = container.querySelector(
            `script[${TFJS_SCRIPTS_ATTR}="tflite"]`,
        ) as HTMLScriptElement | null;

        if (existingTfliteScript) {
            existingTfliteScript.addEventListener('load', markTfliteReady, { once: true });
            existingTfliteScript.addEventListener('error', markTfliteScriptError, { once: true });
            markTfliteReady();

            return;
        }

        if (container.querySelector(`script[${TFJS_SCRIPTS_ATTR}="core"]`)) {
            return;
        }

        const cacheBust = scriptsReloadKey > 0 ? `?retry=${scriptsReloadKey}` : '';

        const tflitePreload = document.createElement('link');

        tflitePreload.rel = 'preload';
        tflitePreload.as = 'script';
        tflitePreload.href = `${TF_LITE_SRC}${cacheBust}`;
        tflitePreload.setAttribute('data-tfjs-tflite-preload', '');
        container.appendChild(tflitePreload);

        const coreScript = document.createElement('script');

        coreScript.src = `${TF_CORE_SRC}${cacheBust}`;
        coreScript.setAttribute(TFJS_SCRIPTS_ATTR, 'core');

        const tfliteScript = document.createElement('script');

        // load script instead of npm cause 0.0.1-alpha.10 has invalid dist in npm
        tfliteScript.src = `${TF_LITE_SRC}${cacheBust}`;
        tfliteScript.setAttribute(TFJS_SCRIPTS_ATTR, 'tflite');

        tfliteScript.onload = markTfliteReady;
        tfliteScript.onerror = markTfliteScriptError;
        coreScript.onerror = markTfliteScriptError;

        coreScript.onload = () => {
            container.appendChild(tfliteScript);
        };

        container.appendChild(coreScript);
    }, [sdkOptions, containerRef, scriptsReloadKey]);

    useEffect(() => {
        if (!sdkOptions?.settings?.face_comparison_required || !isTfliteScriptReady) {
            return undefined;
        }

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | undefined;
        let loadInProgress = false;
        const deadline = Date.now() + FACE_DETECTOR_LOADING_TIMEOUT;

        const markModelsLoadError = () => {
            if (!cancelled) {
                setHasFaceDetectorLoadError(true);

                if (intervalId !== undefined) {
                    clearInterval(intervalId);
                    intervalId = undefined;
                }
            }
        };

        const fetchModels = async (): Promise<boolean> => {
            if (cancelled || loadInProgress || typeof window === 'undefined') {
                return false;
            }

            loadInProgress = true;

            try {
                const {
                    detectorModel,
                    landmarkModel,
                    imageQualityModel: loadedImageQualityModel,
                } = await fetchTfliteFaceModels();

                if (!cancelled) {
                    setHasFaceDetectorLoadError(false);
                    setTfLiteModel(detectorModel);
                    setTfLiteLandmarkModel(landmarkModel);
                    setImageQualityModel(loadedImageQualityModel);

                    return true;
                }
            } catch {
                if (Date.now() >= deadline) {
                    markModelsLoadError();
                }
            } finally {
                loadInProgress = false;
            }

            return false;
        };

        const tick = () => {
            if (cancelled) {
                return;
            }

            if (Date.now() >= deadline) {
                markModelsLoadError();

                return;
            }

            fetchModels()
                .then((ok) => {
                    if (ok && intervalId !== undefined) {
                        clearInterval(intervalId);
                        intervalId = undefined;
                    }
                })
                .catch(() => undefined);
        };

        tick();
        intervalId = setInterval(tick, 500);

        return () => {
            cancelled = true;

            if (intervalId !== undefined) {
                clearInterval(intervalId);
            }
        };
    }, [sdkOptions, isTfliteScriptReady, modelsReloadKey]);

    const value = useMemo(
        () => ({
            tfLiteModel,
            tfLiteLandmarkModel,
            imageQualityModel,
            isTfliteScriptReady,
            hasFaceDetectorLoadError,
            refetchTfliteScripts,
            refetchFaceModels,
        }),
        [
            tfLiteModel,
            tfLiteLandmarkModel,
            imageQualityModel,
            isTfliteScriptReady,
            hasFaceDetectorLoadError,
            refetchTfliteScripts,
            refetchFaceModels,
        ],
    );

    return <FaceDetectorContext.Provider value={value}>{children}</FaceDetectorContext.Provider>;
};

export const useFaceDetectorContext = () => useContext(FaceDetectorContext);
